// Imports
import { FormControl, IconButton, MenuItem, Select, TextField, Typography, useMediaQuery, useTheme, Tooltip as MuiTooltip, Popover } from "@mui/material";
import { Box } from "@mui/system";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getHubSelection } from "../../features/hubSelection";
import { GobikesApi } from "../../config/Apis";
import { parseErrorForAxios } from "../utils/CommonUtils";
import { getAuthCookie } from "../auth/AuthUtil";
import { Colors, DEFAULT_LOADER_STYLES } from "../../config/Constants";
import { DashboardLoader } from "../utils/LocalStateUtils";
import { DateTime } from "luxon";
import { getVendorProfile } from "../../features/vendorProfile";
import { BarChart, CartesianGrid, XAxis, YAxis, Bar, Label, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, TooltipProps } from "recharts";
import { setNavSelection } from "../../features/navSelection";
import styles from "./dashboard.module.css";
import { BarChart as BarChartIcon, Info } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterLuxon from "@mui/lab/AdapterLuxon";
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";

// Types and constants
type DashboardPeriodSelection = "today" | "month" | "last-month" | "week" | "custom";
interface OrderRevenueDetail {
  id: number;
  totalRentalAmount: number;
  vendorRentalAmount: number;
  finalVendorAmount: number;
  totalPayableAmount: number;
  paymentTime: number;
  createdAt: string;
}
interface DayWiseOrderRevenueDetail {
  dayStartTs: number;
  orders: number;
  revenue: number;
}
interface SummaryDetail {
  lastMonthOrders: number;
  currentMonthOrders: number;
  lastMonthRevenue: number;
  currentMonthRevenue: number;
}
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  periodSelection: DashboardPeriodSelection;
  startDate: number;
  endDate: number;
  dataLoadRequired?: boolean;
  summaryLoaded?: boolean;
  summary: SummaryDetail;
  rawData: OrderRevenueDetail[];
  data: {
    currentMonthOrders?: number,
    currentMonthRevenue?: number,
    lastMonthOrders?: number,
    lastMonthRevenue?: number,
    stats?: {
      cancellationReasonStats?: any[],
      rating?: any,
      revenueStats?: {
        totalCount: number,
        totalCountBreakdown: { [k: string]: number },
        revenue: number,
        revenueBreakdown: { [k: string]: number },
        extensionCount: number
        extensionCountBreakdown: { [k: string]: number },
        cancelCount: number,
        cancelCountBreakdown: { [k: string]: number },
        cancelRevenue: number,
        cashCollected: number
      },
      topBikes: any,
      impressions: any,
      underperformingBikes?: {
        bikeId: number,
        bike: string,
        daysRented: number,
        revenue: number,
        orderCount: number,
        impressions: number,
        conversionPercent: number,
        reason: 'NO_PHOTOS' | 'LOW_BOOKING_DAYS' | 'LOW_CONVERSION',
        recommendedAction: 'REDUCE_PRICE' | 'UPDATE_PHOTOS'
      }[],
      todaySchedule?: {
        pickups: number,
        drops: number
      }
    }
  };
  dayWiseData: DayWiseOrderRevenueDetail[];
}
const defaultLocalState: LocalState = {
  periodSelection: "today",
  startDate: DateTime.fromMillis(new Date().getTime()).startOf("day").toMillis(),
  endDate: DateTime.fromMillis(new Date().getTime()).endOf("day").toMillis(),
  summary: {
    lastMonthOrders: 0,
    currentMonthOrders: 0,
    lastMonthRevenue: 0,
    currentMonthRevenue: 0,
  },
  dataLoadRequired: true,
  rawData: [],
  dayWiseData: [],
  loading: true,
  data: {}
};
// Graph colors
const COLORS = ["#f44336", "#e81e63", "#9c27b0", "#673ab7", "#3f51b5", "#2196f3", "#03a9f4", "#00bcd4", "#009688", "#4caf50", "#8bc34a", "#cddc39", "#ffeb3b", "#ffc107", "#ff9800", "#ff5722", "#795548", "#9e9e9e", "#607d8b", "#000000"];

// Cancellation reason to text map
const cancellationReasonToText: { [k: string]: string } = {
  VEHICLE_NOT_AVAILABLE: 'Vehicle is not available',
  VEHICLE_CONDITION_NOT_PROPER: 'Vehicle is not in proper condition',
  DOCUMENTATION_ISSUE: 'Documentation Issue',
  PLAN_CHANGED_OR_CANCELLED: 'Plan got changed/canceled',
  PICKUP_LOCATION_TOO_FAR: 'Pickup Location is too far',
  SHOP_IS_CLOSED: 'Shop is closed',
  WRONG_VEHICLE_BOOKED: 'Wrong vehicle booked',
  WRONG_DATE_BOOKED: 'Wrong date booked',
  PARTNER_NOT_REACHABLE: 'Partner is not reachable/available',
  CUSTOMER_DID_NOT_COME: "Customer didn't come",
  CUSTOMER_DID_NOT_LIKE_VEHICLE: "Customer didn't like vehicle",
  OUTSTATION_TRAVEL_NOT_ALLOWED: 'Outstation travel is not allowed',
  DELIVERY_NOT_POSSIBLE: 'Delivery is not possible',
  OTHERS: 'Others'
}


// Main app component
export function MainDashboardNew(props: { window?: () => Window; drawerWidth: number }) {

  // Local state
  const [localState, setLocalState] = useState<LocalState>(defaultLocalState);
  const [topBikesTab, setTopBikesTab] = useState("count");
  const navigate = useNavigate();

  // Revenue breakdown pop overs
  const [anchorEl, setAnchorEl] = useState<{
    revenue: HTMLButtonElement | null,
    count: HTMLButtonElement | null,
    ext: HTMLButtonElement | null
  }>({ revenue: null, count: null, ext: null });
  const open = {
    revenue: Boolean(anchorEl.revenue),
    count: Boolean(anchorEl.count),
    ext: Boolean(anchorEl.ext)
  };
  const ids = {
    revenue: open.revenue ? 'revenue-popover' : undefined,
    count: open.count ? 'count-popover' : undefined,
    ext: open.ext ? 'ext-popover' : undefined
  };

  // Global state and constants
  const selectedHub = useSelector(getHubSelection);
  const profile = useSelector(getVendorProfile);
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up("md"));
  const isCustomMobile = isPhone && localState.periodSelection === 'custom';
  const dispatch = useDispatch();

  // Set nav state
  useEffect(() => {
    dispatch(setNavSelection(1));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Functions to handle input changes
  const handlePeriodTypeChanged = (newValue: DashboardPeriodSelection) => {
    switch (newValue) {
      case "today": {
        setLocalState({
          ...localState,
          periodSelection: newValue,
          startDate: DateTime.fromMillis(new Date().getTime()).startOf("day").toMillis(),
          endDate: DateTime.fromMillis(new Date().getTime()).endOf("day").toMillis(),
          dataLoadRequired: true,
        });
        break;
      }
      case "month": {
        setLocalState({
          ...localState,
          periodSelection: newValue,
          startDate: DateTime.fromMillis(new Date().getTime()).startOf("month").toMillis(),
          endDate: DateTime.fromMillis(new Date().getTime()).endOf('day').toMillis(),
          dataLoadRequired: true,
        });
        break;
      }
      case "last-month": {
        setLocalState({
          ...localState,
          periodSelection: newValue,
          startDate: DateTime.fromMillis(new Date().getTime()).minus({ months: 1 }).startOf("month").toMillis(),
          endDate: DateTime.fromMillis(new Date().getTime()).minus({ months: 1 }).endOf("month").toMillis(),
          dataLoadRequired: true,
        });
        break;
      }
      case "week": {
        setLocalState({
          ...localState,
          periodSelection: newValue,
          startDate: DateTime.fromMillis(new Date().getTime()).startOf("week").toMillis(),
          endDate: DateTime.fromMillis(new Date().getTime()).endOf("week").toMillis(),
          dataLoadRequired: true,
        });
        break;
      }
      case "custom": {
        setLocalState({
          ...localState,
          periodSelection: newValue,
        });
        break;
      }
      default: {
        console.log("Invalid period selection");
      }
    }
  };
  const handleStartDateChange = (ts: number) => {
    setLocalState({ ...localState, startDate: ts, dataLoadRequired: true });
  }
  const handleEndDateChange = (ts: number) => {
    setLocalState({ ...localState, endDate: ts, dataLoadRequired: true });
  }

  // Stat breakdown popovers
  const handleRevenueClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl({ ...anchorEl, revenue: event.currentTarget });
  };
  const handleRevenueClose = () => {
    setAnchorEl({ ...anchorEl, revenue: null });
  };
  const handleCountClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl({ ...anchorEl, count: event.currentTarget });
  };
  const handleCountClose = () => {
    setAnchorEl({ ...anchorEl, count: null });
  };
  const handleExtClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl({ ...anchorEl, ext: event.currentTarget });
  };
  const handleExtClose = () => {
    setAnchorEl({ ...anchorEl, ext: null });
  };


  // Load orders details on every state change
  const loadOrders = () => {
    const authCookie = getAuthCookie();
    if (!selectedHub?.id || !authCookie) return;
    setLocalState({ ...localState, loading: true, message: "", dataLoadRequired: false });
    (async () => {
      let newLocalState = localState;

      // Fetch order data
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${selectedHub.id}/orders`, {
          params: { revenueDataOnly: true, timeStart: localState.startDate, timeEnd: localState.endDate },
          headers: { "vendor-token": authCookie },
        });
        const fullData: OrderRevenueDetail[] = data.data;
        const dayWiseData = generateDayWiseData(fullData);
        newLocalState = { ...newLocalState, dataLoadRequired: false, rawData: fullData, dayWiseData };
      } catch (e) {
        setLocalState({ ...localState, error: true, message: parseErrorForAxios(e), dataLoadRequired: false });
      }

      // Fetch stats data if required
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${selectedHub.id}/orders/stats`, {
          params: {
            startTime: localState.startDate,
            endTime: localState.endDate,
            statTypes: "revenue,cancellation-reason,top-bikes,rating,impressions,underperforming-bikes,today-schedule",
          },
          headers: { "vendor-token": authCookie },
        });
        newLocalState = { ...newLocalState, dataLoadRequired: false, data: data.data };
      } catch (e) {
        console.log(e);
        setLocalState({ ...localState, error: true, message: parseErrorForAxios(e), dataLoadRequired: false });
      }

      // Set final state
      newLocalState.loading = false;
      setLocalState(newLocalState);
    })();
  };
  useEffect(loadOrders, [localState.periodSelection, localState.startDate, localState.endDate, selectedHub]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCancellationReasonData = () => {
    return localState.data.stats?.cancellationReasonStats?.map((reason: any) => {
      return { name: reason.reason, value: parseInt(reason.count) };
    }) || [];
  };

  const TOP_BIKES_LIMIT = 10;
  const getTopBikesCountData = () => {
    return (localState.data.stats?.topBikes.count.map((bike: any, index: number) => {
      return { name: bike.bike, uv: parseInt(bike.count) };
    }) || []).sort((a: { uv: number }, b: { uv: number }) => b.uv - a.uv).slice(0, TOP_BIKES_LIMIT);
  };
  const getTopBikesRevenueData = () => {
    return (localState.data.stats?.topBikes.revenue.map((bike: any, index: number) => {
      return { name: bike.bike, uv: parseInt(bike.revenue) };
    }) || []).sort((a: { uv: number }, b: { uv: number }) => b.uv - a.uv).slice(0, TOP_BIKES_LIMIT);
  };
  const getTopBikesImpressionsData = () => {
    return (localState.data.stats?.impressions.count.map((bike: any, index: number) => {
      return { name: bike.bike, uv: parseInt(bike.count) };
    }) || []).sort((a: { uv: number }, b: { uv: number }) => b.uv - a.uv).slice(0, TOP_BIKES_LIMIT);
  };
  const getTopBikesConversionData = () => {
    const orderCountByBike: { [name: string]: number } = {};
    (localState.data.stats?.topBikes?.userOrderCount || []).forEach((bike: any) => {
      orderCountByBike[bike.bike] = parseInt(bike.count);
    });
    return (localState.data.stats?.impressions?.count || []).map((bike: any) => {
      const impressionCount = parseInt(bike.count);
      const orderCount = orderCountByBike[bike.bike] || 0;
      const ratio = impressionCount > 0 ? Math.round((orderCount / impressionCount) * 1000) / 10 : 0;
      return { name: bike.bike, uv: ratio };
    }).sort((a: { uv: number }, b: { uv: number }) => b.uv - a.uv).slice(0, TOP_BIKES_LIMIT) || [];
  };
  const getUnderperformingBikesData = () => localState.data.stats?.underperformingBikes || [];

  // Jump to a vehicle's detail page and pre-open the relevant section (discount modal or photo upload)
  const handleRecommendedAction = (bikeId: number, recommendedAction: 'REDUCE_PRICE' | 'UPDATE_PHOTOS') => {
    navigate(`/bikes?bikeId=${bikeId}&action=${recommendedAction === 'REDUCE_PRICE' ? 'discount' : 'photos'}`);
  };

  // Loading view
  let mainContent = <div></div>;
  if (localState.loading) {
    mainContent = <DashboardLoader {...localState} styles={DEFAULT_LOADER_STYLES} />;
  }

  // Error view
  else if (localState.error) {
    mainContent = <DashboardLoader {...localState} styles={DEFAULT_LOADER_STYLES} />;
  }

  // Default dashboard view
  else {
    const topBikesRevenueTab = topBikesTab === "revenue";
    const bucketUnit = getBucketUnit();
    mainContent = (
      <div className={`${styles.dashboard} gbk-scroll`}>
        <div className={styles.row}>
          <div className={styles.overviewContainer}>
            <h2 className={styles.heading}>
              <span className={styles.overviewHeading} >Overview</span>
              <div className={styles.periodSelection} style={isCustomMobile ? { flexWrap: 'wrap' } : undefined}>
                <div className={`${isPhone ? '' : 'inb'} vam`} style={{ float: "right", ...(isCustomMobile ? { width: '100%', order: 2, marginTop: '8px' } : {}) }}>
                  {renderPeriodSelection()}
                </div>
                <div className={`${isPhone ? '' : 'inb'} vam`} style={{ width: isPhone ? '110px' : '170px', marginBottom: isPhone ? '0' : '10px', marginLeft: isPhone ? '6px' : '20px', ...(isCustomMobile ? { order: 1 } : {}) }}>
                  <FormControl fullWidth>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      className="input-border-light input-bg-white input-font-poppins"
                      value={localState.periodSelection}
                      onChange={(e) => handlePeriodTypeChanged(e.target.value as DashboardPeriodSelection)}
                      size={'small'}
                      sx={isPhone ? { fontSize: '12px' } : undefined}
                      MenuProps={isPhone ? { PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '13px', minHeight: '32px' } } } } : undefined}
                    >
                      <MenuItem value={"today"}>Today</MenuItem>
                      <MenuItem value={"month"}>This Month</MenuItem>
                      <MenuItem value={"last-month"}>Last Month</MenuItem>
                      <MenuItem value={"week"}>This Week</MenuItem>
                      <MenuItem value={"custom"}>Custom Range</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </div>
            </h2>

            <hr className={styles.hr} />
            <div className={styles.overview}>
              <div className={styles.overviewItem}>
                <p>REVENUE</p>
                <h3>
                  <span className="roboto">₹</span>
                  {(localState?.data?.stats?.revenueStats?.revenue || 0).toFixed(0)}
                  {localState?.data?.stats?.revenueStats?.revenueBreakdown.VENDOR ? (
                    <Fragment>
                      <IconButton onClick={handleRevenueClick}>
                        <span className="gbk-icon-span fs-16">
                          <Info sx={{ color: Colors.primary }} />
                        </span>
                      </IconButton>
                      <Popover
                        id={ids.revenue}
                        open={open.revenue}
                        anchorEl={anchorEl.revenue}
                        onClose={handleRevenueClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <div>
                          <Box className="inb" sx={{ borderRight: '1px solid #eee' }} >
                            <Box sx={{ p: '10px 10px 5px 10px', backgroundColor: '#f7f7f7' }}><Typography><b>Self</b></Typography> </Box>
                            <Box sx={{ p: '5px 10px 5px 10px', backgroundColor: '#fff' }}><Typography>{(localState?.data?.stats?.revenueStats?.revenueBreakdown.VENDOR || 0).toFixed(0)}</Typography></Box>
                          </Box>
                          <Box className="inb">
                            <Box sx={{ p: '10px 10px 5px 10px', backgroundColor: '#f7f7f7' }}><Typography><b>GoWheelo</b></Typography> </Box>
                            <Box sx={{ p: '5px 10px 5px 10px', backgroundColor: '#fff' }}><Typography>{(localState?.data?.stats?.revenueStats?.revenueBreakdown.USER || 0).toFixed(0)}</Typography></Box>
                          </Box>
                        </div>
                      </Popover>
                    </Fragment>
                  ) : ''}
                </h3>
              </div>
              <div className={styles.overviewItem}>
                <p>CONFIRMED ORDERS</p>
                <h3>
                  {localState?.data?.stats?.revenueStats?.totalCount}
                  {localState?.data?.stats?.revenueStats?.totalCountBreakdown.VENDOR ? (
                    <Fragment>
                      <IconButton onClick={handleCountClick}>
                        <span className="gbk-icon-span fs-16">
                          <Info sx={{ color: Colors.primary }} />
                        </span>
                      </IconButton>
                      <Popover
                        id={ids.count}
                        open={open.count}
                        anchorEl={anchorEl.count}
                        onClose={handleCountClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <div>
                          <Box className="inb" sx={{ borderRight: '1px solid #eee' }} >
                            <Box sx={{ p: '10px 10px 5px 10px', backgroundColor: '#f7f7f7', }}><Typography><b>Self</b></Typography> </Box>
                            <Box sx={{ p: '5px 10px 5px 10px', backgroundColor: '#fff' }}><Typography>{(localState?.data?.stats?.revenueStats?.totalCountBreakdown.VENDOR || 0).toFixed(0)}</Typography></Box>
                          </Box>
                          <Box className="inb">
                            <Box sx={{ p: '10px 10px 5px 10px', backgroundColor: '#f7f7f7', }}><Typography><b>GoWheelo</b></Typography> </Box>
                            <Box sx={{ p: '5px 10px 5px 10px', backgroundColor: '#fff' }}><Typography>{(localState?.data?.stats?.revenueStats?.totalCountBreakdown.USER || 0).toFixed(0)}</Typography></Box>
                          </Box>
                        </div>
                      </Popover>
                    </Fragment>
                  ) : ''}
                </h3>
              </div>
              <div className={styles.overviewItem}>
                <p>EXTENDED ORDERS</p>
                <h3>
                  {localState.data.stats?.revenueStats?.extensionCount}
                  {localState?.data?.stats?.revenueStats?.extensionCountBreakdown.VENDOR ? (
                    <Fragment>
                      <IconButton onClick={handleExtClick}>
                        <span className="gbk-icon-span fs-16" >
                          <Info sx={{ color: Colors.primary }} />
                        </span>
                      </IconButton>
                      <Popover
                        id={ids.ext}
                        open={open.ext}
                        anchorEl={anchorEl.ext}
                        onClose={handleExtClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <div>
                          <Box className="inb" sx={{ borderRight: '1px solid #eee' }} >
                            <Box sx={{ p: '10px 10px 5px 10px', backgroundColor: '#f7f7f7', }}><Typography><b>Self</b></Typography> </Box>
                            <Box sx={{ p: '5px 10px 5px 10px', backgroundColor: '#fff' }}><Typography>{(localState?.data?.stats?.revenueStats?.extensionCountBreakdown.VENDOR || 0).toFixed(0)}</Typography></Box>
                          </Box>
                          <Box className="inb">
                            <Box sx={{ p: '10px 10px 5px 10px', backgroundColor: '#f7f7f7', }}><Typography><b>GoWheelo</b></Typography> </Box>
                            <Box sx={{ p: '5px 10px 5px 10px', backgroundColor: '#fff' }}><Typography>{(localState?.data?.stats?.revenueStats?.extensionCountBreakdown.USER || 0).toFixed(0)}</Typography></Box>
                          </Box>
                        </div>
                      </Popover>
                    </Fragment>
                  ) : ''}
                </h3>
              </div>
              <div className={styles.overviewItem}>
                <p>CANCELLED ORDERS</p>
                <h3>{localState.data.stats?.revenueStats?.cancelCount}</h3>
              </div>
              <div className={styles.overviewItem}>
                <p>OVERALL RATING</p>
                <h3>{Math.round((localState.data.stats?.rating?.avgRating || 0) * 10) / 10}</h3>
              </div>
              <div className={styles.overviewItem}>
                <p>RATING COUNT</p>
                <h3>{localState.data.stats?.rating?.count || 0}</h3>
              </div>
              <div className={styles.overviewItem}>
                <p>TOTAL CASH COLLECTED</p>
                <h3>
                  <span className="roboto">₹</span>
                  {(localState.data.stats?.revenueStats?.cashCollected || 0).toFixed(0)}
                </h3>
              </div>
              {localState.periodSelection === 'today' && (
                <Fragment>
                  <div className={styles.overviewItem}>
                    <p>TODAY'S PICKUPS</p>
                    <h3>{localState.data.stats?.todaySchedule?.pickups || 0}</h3>
                  </div>
                  <div className={styles.overviewItem}>
                    <p>TODAY'S DROPS</p>
                    <h3>{localState.data.stats?.todaySchedule?.drops || 0}</h3>
                  </div>
                </Fragment>
              )}
              {/* <div className={styles.overviewItem}>
                <p>AVG. RATING</p>
                <h3>
                  4.51 <span></span>
                </h3>
              </div> */}
            </div>
          </div>
        </div >

        {/* History and cancellation reason row */}
        <div className={styles.row}>
          <div className={styles.ordersHistory}>
            <h2 className={styles.heading}>
              Orders History
            </h2>
            <hr className={styles.hr} />
            {localState.dayWiseData && localState.dayWiseData.length ? (
              <ResponsiveContainer width="95%" height={isPhone ? 200 : 350}>
                <BarChart data={localState.dayWiseData} margin={isPhone ? { top: 10, bottom: 5, left: 5, right: 10 } : { bottom: 50, left: 50, right: 20 }}>
                  <CartesianGrid strokeDasharray="2 7" vertical={false} />
                  <XAxis
                    dataKey="dayStartTs"
                    tickFormatter={(a) => DateTime.fromMillis(a).toFormat(bucketUnit === "month" ? "MMM yyyy" : "dd MMM")}
                    minTickGap={50}
                    tickMargin={isPhone ? 8 : 12}
                    tick={isPhone ? { fontSize: 10 } : undefined}
                  >
                    {!isPhone && <Label value={"Date"} position="insideBottom" fontSize={18} offset={-30} />}
                  </XAxis>
                  <YAxis tickFormatter={a => `₹ ${a}`} tick={isPhone ? { fontSize: 10 } : undefined} width={isPhone ? 45 : undefined}>
                    {!isPhone && <Label value={"Revenue"} position="insideLeft" fontSize={18} offset={-30} angle={-90} />}
                  </YAxis>
                  {/* <Tooltip /> */}
                  <Tooltip
                    content={<CustomTooltip bucketUnit={bucketUnit} />}
                  />
                  <Bar dataKey="revenue" fill="#bbdd77" radius={[4, 4, 4, 4]} barSize={isPhone ? 10 : 15} name="Revenue (₹)" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div
                className="poppins"
                style={{ height: 300, paddingTop: 200, textAlign: "center", color: Colors.greyDark3 }}
              >
                No data loaded
              </div>
            )}
          </div>
          <div className={styles.cancellationReason}>
            <h2 className={styles.heading}>Cancellation Reason</h2>
            <hr className={styles.hr} />
            {getCancellationReasonData().length ? (
              <Fragment>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={getCancellationReasonData()}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#82ca9d"
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {getCancellationReasonData().map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className={styles.centeredText}>
                  <h2>{localState.data.stats?.revenueStats?.cancelCount}</h2>
                  <p>Total</p>
                </div>
                <div className={styles.legend}>
                  {getCancellationReasonData().map((entry: any, index: number) => (
                    <div key={`legend-${index}`} className={styles.legendItem}>
                      <span className={styles.colorBox} style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                      <span className={styles.label}>{cancellationReasonToText[entry.name] || entry.name}</span>
                      <span className={styles.value}>{entry.value}</span>
                    </div>
                  ))}
                </div>
              </Fragment>
            ) : (
              <div className="poppins" style={{ padding: "20px", textAlign: "center", color: Colors.greyDark3 }}>
                No cancellations in this period
              </div>
            )}
          </div>
        </div>

        {/* Latest Reviews and Tuts row */}
        {/* <div className={styles.row}>
          <div className={styles.customerReviews}>
            <h2 className={styles.heading}>Latest Customer Reviews</h2>
            <hr className={styles.hr} />
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Bike Name</th>
                  <th>Rating</th>
                  <th style={{ maxWidth: "300px" }}>Comments</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review, index) => (
                  <tr key={index}>
                    <td>{review.date}</td>
                    <td>{review.bike}</td>
                    <td>{review.rating}/5</td>
                    <td style={{ maxWidth: "300px", textAlign: "left" }}>{review.comments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.tutorials}>
            <h2 className={styles.heading}>Tutorials</h2>
            <hr className={styles.hr} />
            <img alt="tutorials" src={TutorialImg} width="198" />
            <div>
              <h3>How to Use GoBikes?</h3>
              <button>
                Watch Tutorials <img alt="watch" src={WatchIcon} width={20} />
              </button>
            </div>
          </div>
        </div> */}

        {/* Top bikes and underperforming bikes row */}
        <div className={styles.row}>
          <div className={styles.topBikes}>
            <div className={styles.heading}>
              <h2 style={{ fontWeight: 'bold' }}>Top Vehicles</h2>
              <FormControl size="small" sx={{ minWidth: isPhone ? 140 : 220 }}>
                <Select
                  value={topBikesTab}
                  onChange={(e) => setTopBikesTab(e.target.value as string)}
                >
                  <MenuItem value="count">Order Count</MenuItem>
                  <MenuItem value="revenue">Revenue Generated</MenuItem>
                  <MenuItem value="impressions">Impressions</MenuItem>
                  <MenuItem value="conversion">Impression-to-Book Ratio</MenuItem>
                </Select>
              </FormControl>
            </div>
            <hr className={styles.hr} />
            {(() => {
              const bikeData = topBikesTab === "count" ? getTopBikesCountData() : topBikesTab === "revenue" ? getTopBikesRevenueData() : topBikesTab === "impressions" ? getTopBikesImpressionsData() : getTopBikesConversionData();
              const chartHeight = isPhone ? Math.max(150, bikeData.length * 35 + 45) : Math.max(150, bikeData.length * 45 + 60);
              const topBikesConversionTab = topBikesTab === "conversion";
              return (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
                    data={bikeData}
                    layout="vertical"
                    margin={{ right: isPhone ? 25 : 40, bottom: isPhone ? 20 : 20, top: isPhone ? 5 : 0 }}
                  >
                    <XAxis type="number" domain={[0, 'dataMax']} allowDecimals={topBikesConversionTab} tickFormatter={a => topBikesRevenueTab ? `₹ ${a}` : topBikesConversionTab ? `${a}%` : a} tick={{ fontSize: isPhone ? 10 : 12 }} />
                    <YAxis dataKey="name" type="category" width={isPhone ? 110 : 150} tickFormatter={(value) => `${value}`} fontSize={isPhone ? 10 : 12} />
                    <Bar dataKey="uv" fill="#99cc33" radius={[4, 4, 4, 4]} barSize={isPhone ? 14 : 20} />
                    <Tooltip
                      cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                      content={topBikesTab === "count" ? <TopBikesCountTooltip /> : topBikesTab === "revenue" ? <TopBikesRevenueTooltip /> : topBikesTab === "impressions" ? <TopBikesImpressionsTooltip /> : <TopBikesConversionTooltip />}
                    />
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </div>

          {/* Underperforming vehicles */}
          <div className={styles.underperformingBikes}>
            <h2 className={styles.heading}>Underperforming Vehicles</h2>
            <hr className={styles.hr} />
            {(() => {
              const underperformingBikes = getUnderperformingBikesData();
              if (!underperformingBikes.length) {
                return (
                  <div className="poppins" style={{ padding: '20px', textAlign: "center", color: Colors.greyDark3 }}>
                    No underperforming vehicles in the last 30 days
                  </div>
                );
              }
              return (
                <div style={{ overflowX: 'auto', padding: '0 10px 10px' }}>
                  <table className={styles.underperformingTable}>
                    <thead>
                      <tr>
                        <th>Vehicle Name</th>
                        <th>Days Rented (Last 30 Days)</th>
                        <th>Revenue Generated</th>
                        <th>Impression-to-Book %</th>
                        <th>Recommended Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {underperformingBikes.map((bike, i) => (
                        <tr key={i}>
                          <td>{bike.bike}</td>
                          <td>{bike.daysRented} Days</td>
                          <td>₹{bike.revenue}</td>
                          <td>{bike.impressions ? `${bike.conversionPercent}%` : '-'}</td>
                          <td>
                            <button
                              className={styles.underperformingActionBtn}
                              onClick={() => handleRecommendedAction(bike.bikeId, bike.recommendedAction)}
                            >
                              {bike.recommendedAction === 'REDUCE_PRICE' ? 'Reduce Price by 10%' : 'Update Photos'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </div>
      </div >
    );
  }

  // Return DOM
  return (
    <div className={styles.root}>
      <Box
        sx={{
          mt: { xs: 10, md: 0 },
          ml: { xs: 0, md: `${props.drawerWidth}px` },
          width: { xs: "100%", md: `calc(100% - ${props.drawerWidth}px)` },
          height: "100%",
        }}
      >
        <Box className={`gbk-tabcontent allow-scroll`}>

          {/* Header */}
          <Box className='tab-header' sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              {/* Icon */}
              <div className={`tab-header-fab`} >
                <BarChartIcon fontSize="large" />
              </div>

              {/* Text */}
              <Typography>
                Analytics
              </Typography>
            </Box>

            {/* Current balance */}
            <Box sx={{ textAlign: 'right', pr: '20px' }}>
              <Typography noWrap component="div" sx={{ fontSize: '13px', fontWeight: 600, color: Colors.primary, lineHeight: 1.4 }}>
                Current Balance
              </Typography>
              <Typography noWrap component="div" sx={{ fontSize: '20px', fontWeight: 600, color: Colors.primary }}>
                <span className="roboto">₹</span> {(profile.cashfreeData?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>

          {/* Main content */}
          {mainContent}
        </Box>
      </Box>
    </div>
  );

  // Utility function to decide bar granularity based on the selected range length
  function getBucketUnit(): "day" | "week" | "month" {
    const rangeDays = DateTime.fromMillis(localState.endDate).diff(DateTime.fromMillis(localState.startDate), "days").days;
    if (rangeDays > 180) return "month";
    if (rangeDays > 45) return "week";
    return "day";
  }

  // Utility function to process data into required format
  function generateDayWiseData(rawData: OrderRevenueDetail[]): DayWiseOrderRevenueDetail[] {
    const dayWiseMap: { [key: number]: { orders: number; revenue: number } } = {};

    let currentDay = localState.startDate;
    while (currentDay <= localState.endDate) {
      dayWiseMap[currentDay] = { orders: 0, revenue: 0 };
      currentDay = DateTime.fromMillis(currentDay).plus({ day: 1 }).toMillis();
    }

    for (const datum of rawData) {
      if (!datum.paymentTime) {
        datum.paymentTime = new Date(datum.createdAt).getTime();
      }
      const dayStart = DateTime.fromMillis(Number(datum.paymentTime)).startOf("day").toMillis();
      if (!dayWiseMap.hasOwnProperty(dayStart)) {
        dayWiseMap[dayStart] = { orders: 1, revenue: datum.totalPayableAmount };
      } else {
        dayWiseMap[dayStart].orders++;
        dayWiseMap[dayStart].revenue += datum.totalPayableAmount;
      }
    }

    // Roll daily totals up into week/month buckets for long ranges, so the chart isn't mostly empty bars
    const bucketUnit = getBucketUnit();
    const bucketMap: { [key: number]: { orders: number; revenue: number } } = {};
    for (const day of Object.keys(dayWiseMap)) {
      const dayTs = Number(day);
      const bucketStart = bucketUnit === "day" ? dayTs : DateTime.fromMillis(dayTs).startOf(bucketUnit).toMillis();
      if (!bucketMap.hasOwnProperty(bucketStart)) {
        bucketMap[bucketStart] = { orders: 0, revenue: 0 };
      }
      bucketMap[bucketStart].orders += dayWiseMap[dayTs].orders;
      bucketMap[bucketStart].revenue += dayWiseMap[dayTs].revenue;
    }

    const result: DayWiseOrderRevenueDetail[] = [];
    for (const bucket of Object.keys(bucketMap)) {
      result.push({
        dayStartTs: Number(bucket),
        orders: bucketMap[Number(bucket)].orders,
        revenue: bucketMap[Number(bucket)].revenue,
      });
    }
    result.sort((a, b) => a.dayStartTs - b.dayStartTs);
    return result;
  }

  // Utility function to render period selection
  function renderPeriodSelection() {
    if (localState.periodSelection === 'custom') {
      const pickerSx = isPhone ? { '& .MuiInputBase-input': { fontSize: '12px', padding: '6.5px 8px' } } : undefined;
      const pickerFormat = isPhone ? 'dd MMM yy' : 'dd MMM yyyy';
      const startPicker = (
        <FormControl fullWidth={isPhone} className='input-border-light input-bg-white input-font-poppins'>
          <LocalizationProvider dateAdapter={AdapterLuxon} >
            <DatePicker
              renderInput={(props) => <TextField {...props} size={'small'} sx={pickerSx} />}
              onChange={(e: any) => handleStartDateChange(e.ts)}
              value={DateTime.fromMillis(localState.startDate)}
              inputFormat={pickerFormat}
            />
          </LocalizationProvider>
        </FormControl>
      );
      const endPicker = (
        <FormControl fullWidth={isPhone} className='input-border-light input-bg-white input-font-poppins'>
          <LocalizationProvider dateAdapter={AdapterLuxon} >
            <DatePicker
              renderInput={(props) => <TextField {...props} size={'small'} sx={pickerSx} />}
              onChange={(e: any) => handleEndDateChange(e.ts)}
              value={DateTime.fromMillis(localState.endDate)}
              inputFormat={pickerFormat}
            />
          </LocalizationProvider>
        </FormControl>
      );
      if (isPhone) {
        return (
          <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
            <Box className='poppins' sx={{ flex: 1, minWidth: 0 }}>{startPicker}</Box>
            <Box className='poppins' sx={{ flex: 1, minWidth: 0 }}>{endPicker}</Box>
          </Box>
        )
      }
      return (
        <Fragment>
          <Box className='poppins' sx={{ width: '170px', ml: '10px', display: 'inline-block' }}>{startPicker}</Box>
          <Box className='poppins' sx={{ width: '170px', ml: '10px', display: 'inline-block' }}>{endPicker}</Box>
        </Fragment>
      )
    } else {
      return (
        <Box className='poppins' sx={{ padding: { xs: '0 4px', md: '0 16px' }, fontSize: { xs: '11px', md: '14px' }, whiteSpace: { xs: 'nowrap', md: 'normal' }, paddingLeft: { xs: '0', md: '16px', lineHeight: '38px' } }}>
          {DateTime.fromMillis(localState.startDate).toFormat(isPhone ? 'dd MMM' : 'dd MMM yyyy')} - {DateTime.fromMillis(localState.endDate).toFormat(isPhone ? 'dd MMM' : 'dd MMM yyyy')}
        </Box>
      )
    }
  }
}


// Custom tooltip component
function CustomTooltip({
  active,
  payload,
  label,
  bucketUnit
}: TooltipProps<ValueType, NameType> & { bucketUnit?: "day" | "week" | "month" }): JSX.Element {
  if (active && payload && payload[0].payload && payload[0].payload) {
    const bucketStart = DateTime.fromMillis(payload[0].payload.dayStartTs);
    const periodLabel = bucketUnit === "month"
      ? bucketStart.toFormat('MMMM yyyy')
      : bucketUnit === "week"
        ? `Week of ${bucketStart.toFormat('dd MMM yyyy')}`
        : bucketStart.toFormat('dd MMM yyyy');
    return (
      <div className="custom-tooltip">
        <p className="label poppins">{periodLabel}</p>
        <p className="desc poppins">Orders: <b className='primary-text'>{payload[0].payload.orders}</b> </p>
        <p className="desc poppins">Revenue: <b className='primary-text'><span className='roboto'>₹</span> {Number(payload[0].payload.revenue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></p>
      </div>
    );
  }
  return <div></div>;
};

// Top bikes count tooltip
function TopBikesCountTooltip({
  active,
  payload,
  label
}: TooltipProps<ValueType, NameType>): JSX.Element {
  if (active && payload && payload[0].payload && payload[0].payload) {
    return (
      <div className="custom-tooltip">
        <p className="desc poppins"><b>{label}</b> </p>
        <p className="desc poppins">Order count: <b className='primary-text'> {Number(payload[0].payload.uv)}</b></p>
      </div>
    );
  }
  return <div></div>;
};


// Top bikes revenue tooltip
function TopBikesRevenueTooltip({
  active,
  payload,
  label
}: TooltipProps<ValueType, NameType>): JSX.Element {
  if (active && payload && payload[0].payload && payload[0].payload) {
    return (
      <div className="custom-tooltip">
        <p className="desc poppins"><b>{label}</b> </p>
        <p className="desc poppins">Revenue: <b className='primary-text'><span className='roboto'>₹</span> {Number(payload[0].payload.uv).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></p>
      </div>
    );
  }
  return <div></div>;
};

// Top bikes impressions tooltip
function TopBikesImpressionsTooltip({
  active,
  payload,
  label
}: TooltipProps<ValueType, NameType>): JSX.Element {
  if (active && payload && payload[0].payload && payload[0].payload) {
    return (
      <div className="custom-tooltip">
        <p className="desc poppins"><b>{label}</b> </p>
        <p className="desc poppins">Impressions: <b className='primary-text'> {Number(payload[0].payload.uv)}</b></p>
      </div>
    );
  }
  return <div></div>;
};

// Top bikes impression-to-book ratio tooltip
function TopBikesConversionTooltip({
  active,
  payload,
  label
}: TooltipProps<ValueType, NameType>): JSX.Element {
  if (active && payload && payload[0].payload && payload[0].payload) {
    return (
      <div className="custom-tooltip">
        <p className="desc poppins"><b>{label}</b> </p>
        <p className="desc poppins">Impression-to-Book Ratio: <b className='primary-text'> {Number(payload[0].payload.uv)}%</b></p>
      </div>
    );
  }
  return <div></div>;
};